import React, { type PropsWithChildren, useRef } from 'react';
import { ApplicationView as view } from '@aha-app/mvc';
import { useDrop } from 'react-dnd';
import type ListDropHandler from '../ListDropHandler';
import { type SelectableItemController } from '../SelectableItem';
import { sharedDragController } from '../controllers/DragController';

type ListPreviewDropTargetProps = {
  className?: string;
  // The drop handler for the preview drop target.
  dropHandler: ListDropHandler<SelectableItemController>;
};

/**
 * The ListPreviewDropTarget is used as the drop target for drag preview items in a list.
 * Since the drag preview items are often where the final drop actually occurs, this
 * allows the drop to correctly finish.
 */
const ListPreviewDropTarget: React.FC<
  PropsWithChildren<ListPreviewDropTargetProps>
> = ({ children, className = null, dropHandler }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: dropHandler.accepts,
    canDrop(draggedItem, monitor) {
      // This is an item that is being previewed in this location, so it must be droppable here.
      return true;
    },
    hover() {
      // no op
    },
    drop(draggedItem, monitor) {
      if (!monitor.didDrop()) {
        sharedDragController.actionFinishDrop();
      }
    },
  });

  drop(ref);

  return (
    <div className={className ?? undefined} ref={ref}>
      {children}
    </div>
  );
};

export default view(ListPreviewDropTarget);
